import { SyncOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Flex, Input, Row, Segmented, Select, Space, Statistic, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import { Link } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { RefreshButton } from "@/components/RefreshButton";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PlatformLabel } from "../channels/PlatformBadge";

type Store = Schemas["ReceivableStore"];
type Order = Schemas["ReceivableOrder"];
type Tab = "orders" | "statements" | "payouts";

/**
 * Piutang marketplace (Plan §6.19, part D): what each marketplace still owes
 * (orders shipped and not settled, by age), what it settled (revenue, fees,
 * net into the store balance) and paid out to the bank.
 */
export function Receivables() {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { can, me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const manage = can("finance.manage");
  const [tab, setTab] = useUrlFilter<Tab>("tab", "orders", ["orders", "statements", "payouts"]);
  const [store, setStore] = useUrlFilter<string>("store", "");
  const stores = useQuery({ queryKey: ["finance", "receivables", "stores"], queryFn: () => unwrap(api.GET("/v1/finance/receivables")) });
  const accounts = useQuery({ queryKey: ["finance", "cash", "accounts"], queryFn: () => unwrap(api.GET("/v1/finance/cash/accounts")) });
  const banks = (accounts.data?.items ?? []).filter((a) => a.active && a.kind !== "marketplace");
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["finance"] });
  const sync = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/finance/receivables/stores/{connectionID}/sync", { params: { path: { connectionID: id } } })),
    onSuccess: () => (refresh(), void message.success(t("ar.synced"))),
    onError: (err) => (refresh(), void message.error(errorText(err))),
  });
  const setPayout = useMutation({
    mutationFn: (v: { id: string; account: string | null }) =>
      unwrap(api.PUT("/v1/finance/receivables/stores/{connectionID}", { params: { path: { connectionID: v.id } }, body: { payoutAccountId: v.account } })),
    onSuccess: () => (refresh(), void message.success(t("ar.payoutSaved"))),
    onError: (err) => void message.error(errorText(err)),
  });
  const items = stores.data?.items ?? [];
  const total = items.reduce(
    (a, s) => ({ owed: a.owed + s.unsettledAmount, settled: a.settled + s.settled, fees: a.fees + s.fees, revenue: a.revenue + s.revenue }),
    { owed: 0, settled: 0, fees: 0, revenue: 0 },
  );
  const feePct = total.revenue > 0 ? ((-total.fees / total.revenue) * 100).toFixed(1) : "—";
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={0}>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("ar.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("ar.subtitle")}</Typography.Text>
        </Space>
        <RefreshButton queryKeys={[["finance", "receivables"]]} />
      </Flex>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title={t("ar.owed")} value={formatMoney(total.owed)} />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic title={t("ar.settledMonth")} value={formatMoney(total.settled)} />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic title={t("ar.feesMonth", { pct: feePct })} value={formatMoney(-total.fees)} />
          </Card>
        </Col>
      </Row>
      <Table<Store>
        rowKey="connectionId"
        size="small"
        loading={stores.isPending}
        dataSource={items}
        pagination={false}
        style={{ marginBottom: 24 }}
        locale={{ emptyText: t("ar.noStores") }}
        columns={[
          {
            title: t("orders.store"),
            render: (_, s) => (
              <Space orientation="vertical" size={0}>
                <Space>
                  <PlatformLabel platform={s.platform} />
                  <Typography.Text strong>{s.name}</Typography.Text>
                </Space>
                {!s.supported ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("ar.unsupported")}
                  </Typography.Text>
                ) : s.lastError ? (
                  <Tooltip title={s.lastError}>
                    <Typography.Text type="danger" style={{ fontSize: 12 }}>
                      {t("ar.syncFailed")}
                    </Typography.Text>
                  </Tooltip>
                ) : (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {s.syncedAt ? t("ar.syncedAt", { at: formatDateTime(s.syncedAt, tz) }) : t("ar.neverSynced")}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: t("ar.owedCol"),
            width: 230,
            render: (_, s) => (
              <Space orientation="vertical" size={2}>
                <Typography.Text strong className="z-num">
                  {formatMoney(s.unsettledAmount)}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("ar.ordersN", { n: s.unsettledOrders })}
                </Typography.Text>
                {s.estimatedOrders > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("ar.estimatedNet", { amount: formatMoney(s.estimatedNet), n: s.estimatedOrders })}
                  </Typography.Text>
                )}
                {s.unsettledAmount > 0 && (
                  <Space size={2} wrap>
                    {s.aging.days14 > 0 && <Tag color="gold">8–14h {formatMoney(s.aging.days14)}</Tag>}
                    {s.aging.days30 > 0 && <Tag color="orange">15–30h {formatMoney(s.aging.days30)}</Tag>}
                    {s.aging.older > 0 && <Tag color="red">&gt;30h {formatMoney(s.aging.older)}</Tag>}
                  </Space>
                )}
              </Space>
            ),
          },
          {
            title: t("ar.settledCol"),
            width: 200,
            align: "right",
            render: (_, s) => (
              <Space orientation="vertical" size={0} style={{ alignItems: "flex-end" }}>
                <Typography.Text className="z-num">{formatMoney(s.settled)}</Typography.Text>
                {s.revenue > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("ar.feeLine", { fees: formatMoney(-s.fees), pct: ((-s.fees / s.revenue) * 100).toFixed(1) })}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          { title: t("ar.balance"), width: 150, align: "right", render: (_, s) => <span className="z-num">{formatMoney(s.balance)}</span> },
          {
            title: t("ar.payoutTo"),
            width: 220,
            render: (_, s) => (
              <Select<string>
                size="small"
                allowClear
                disabled={!manage || !s.supported}
                value={s.payoutAccountId ?? undefined}
                placeholder={t("ar.payoutPick")}
                style={{ width: 200 }}
                onChange={(v) => setPayout.mutate({ id: s.connectionId, account: v ?? null })}
                options={banks.map((a) => ({ value: a.id, label: a.name }))}
              />
            ),
          },
          {
            title: "",
            width: 110,
            render: (_, s) =>
              manage &&
              s.supported && (
                <Button size="small" icon={<SyncOutlined />} loading={sync.isPending && sync.variables === s.connectionId} onClick={() => sync.mutate(s.connectionId)}>
                  {t("ar.sync")}
                </Button>
              ),
          },
        ]}
      />
      {banks.length === 0 && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("ar.noBank")} />}
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as Tab)}
        tabBarExtraContent={
          <Select<string>
            value={store}
            onChange={setStore}
            style={{ width: 220 }}
            options={[{ value: "", label: t("orders.allStores") }, ...items.map((s) => ({ value: s.connectionId, label: s.name }))]}
          />
        }
        items={[
          { key: "orders", label: t("ar.tab.orders"), children: <OrdersTab store={store} /> },
          { key: "statements", label: t("ar.tab.statements"), children: <StatementsTab store={store} /> },
          { key: "payouts", label: t("ar.tab.payouts"), children: <PayoutsTab store={store} /> },
        ]}
      />
    </>
  );
}

function OrdersTab({ store }: { store: string }) {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  const page = useListPage();
  const [state, setState] = useUrlFilter<"" | "settled" | "unsettled">("state", "unsettled", ["", "settled", "unsettled"]);
  const [q, setQ] = useUrlFilter<string>("q", "");
  const filters = { ...(store ? { connectionId: store } : {}), ...(state ? { state } : {}), ...(q ? { q } : {}) };
  const list = useQuery({
    queryKey: ["finance", "receivables", "orders", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/finance/receivables/orders", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  return (
    <Space orientation="vertical" size={12} style={{ width: "100%" }}>
      <Flex gap={12} wrap>
        <Segmented<"" | "settled" | "unsettled">
          value={state}
          onChange={setState}
          options={[
            { value: "unsettled", label: t("ar.state.unsettled") },
            { value: "settled", label: t("ar.state.settled") },
            { value: "", label: t("orders.all") },
          ]}
        />
        <Input.Search allowClear defaultValue={q} placeholder={t("orders.number")} onSearch={(v) => setQ(v.trim())} style={{ width: 240 }} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/finance/receivables/orders/export"
          query={filters}
          filename={t("ar.title")}
          columns={[
            { key: "number", label: t("orders.number") },
            { key: "shopName", label: t("orders.store") },
            { key: "orderedAt", label: t("ar.ordered"), kind: "datetime" },
            { key: "status", label: t("common.status") },
            { key: "total", label: t("ar.total"), kind: "number" },
            { key: "revenue", label: t("ar.revenue"), kind: "number" },
            { key: "fees", label: t("ar.fees"), kind: "number" },
            { key: "other", label: t("ar.other"), kind: "number" },
            { key: "net", label: t("ar.net"), kind: "number" },
            { key: "settledAt", label: t("ar.settledAt"), kind: "datetime" },
            { key: "estimate.net", label: t("ar.estimateNet"), kind: "number" },
            { key: "estimate.releaseAt", label: t("ar.estimateRelease"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<Order>
        rowKey="id"
        size="small"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("ar.noOrders") }}
        columns={[
          {
            title: t("orders.number"),
            render: (_, o) => (
              <Space orientation="vertical" size={0}>
                <Link to={`/app/orders/${o.id}`} className="z-num">
                  {o.number}
                </Link>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {o.shopName} · {o.orderedAt ? formatDateTime(o.orderedAt, tz) : "—"}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("ar.total"), dataIndex: "total", width: 130, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
          {
            title: t("ar.fees"),
            width: 130,
            align: "right",
            render: (_, o) => (o.settled ? <span className="z-num">{formatMoney(o.fees)}</span> : "—"),
          },
          {
            title: t("ar.other"),
            width: 120,
            align: "right",
            render: (_, o) => (o.settled && o.other !== 0 ? <span className="z-num">{formatMoney(o.other)}</span> : "—"),
          },
          {
            title: t("ar.net"),
            width: 140,
            align: "right",
            render: (_, o) =>
              o.settled ? (
                <Typography.Text strong className="z-num">
                  {formatMoney(o.net)}
                </Typography.Text>
              ) : o.estimate ? (
                <Tooltip title={t("ar.estimateHelp")}>
                  <Typography.Text type="secondary" className="z-num">
                    ≈ {formatMoney(o.estimate.net)}
                  </Typography.Text>
                </Tooltip>
              ) : (
                <Tag color="warning">{t("ar.state.unsettled")}</Tag>
              ),
          },
          {
            title: t("ar.settledAt"),
            width: 170,
            render: (_, o) =>
              o.settledAt ? (
                formatDateTime(o.settledAt, tz)
              ) : o.estimate?.releaseAt ? (
                <Typography.Text type="secondary">≈ {formatDateTime(o.estimate.releaseAt, tz)}</Typography.Text>
              ) : o.estimate?.reason ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {o.estimate.reason}
                </Typography.Text>
              ) : (
                "—"
              ),
          },
        ]}
      />
    </Space>
  );
}

function StatementsTab({ store }: { store: string }) {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { me } = useAuth();
  const page = useListPage();
  const list = useQuery({
    queryKey: ["finance", "receivables", "statements", store, page.query],
    queryFn: () => unwrap(api.GET("/v1/finance/receivables/statements", { params: { query: { ...(store ? { connectionId: store } : {}), ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  return (
    <Table
      rowKey="id"
      size="small"
      loading={list.isFetching && !list.data}
      dataSource={list.data?.items ?? []}
      pagination={page.pagination(list.data)}
      locale={{ emptyText: t("ar.noStatements") }}
      columns={[
        { title: t("cash.date"), dataIndex: "statementAt", width: 170, render: (v: string) => formatDateTime(v, me?.workspace?.timezone) },
        {
          title: t("ar.statement"),
          render: (_, s) => (
            <Space orientation="vertical" size={0}>
              <Typography.Text className="z-num">{s.externalId}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {s.shopName} · {t("ar.linesN", { n: s.lines })}
                {s.unmatched > 0 && ` · ${t("ar.unmatchedN", { n: s.unmatched })}`}
              </Typography.Text>
            </Space>
          ),
        },
        { title: t("ar.revenue"), dataIndex: "revenue", width: 140, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
        { title: t("ar.fees"), dataIndex: "fees", width: 130, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
        { title: t("ar.adjustment"), dataIndex: "adjustment", width: 120, align: "right", render: (n: number) => (n ? <span className="z-num">{formatMoney(n)}</span> : "—") },
        {
          title: t("ar.net"),
          dataIndex: "settlement",
          width: 150,
          align: "right",
          render: (n: number) => (
            <Typography.Text strong className="z-num">
              {formatMoney(n)}
            </Typography.Text>
          ),
        },
        { title: t("common.status"), dataIndex: "paymentStatus", width: 120, render: (v: string) => (v ? <Tag>{v}</Tag> : "—") },
      ]}
    />
  );
}

function PayoutsTab({ store }: { store: string }) {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { me } = useAuth();
  const list = useQuery({
    queryKey: ["finance", "receivables", "payouts", store],
    queryFn: () => unwrap(api.GET("/v1/finance/receivables/payouts", { params: { query: store ? { connectionId: store } : {} } })),
  });
  return (
    <Table
      rowKey="id"
      size="small"
      loading={list.isPending}
      dataSource={list.data?.items ?? []}
      pagination={false}
      locale={{ emptyText: t("ar.noPayouts") }}
      columns={[
        { title: t("cash.date"), dataIndex: "paidAt", width: 170, render: (v: string) => formatDateTime(v, me?.workspace?.timezone) },
        {
          title: t("ar.payout"),
          render: (_, p) => (
            <Space orientation="vertical" size={0}>
              <Typography.Text className="z-num">{p.externalId}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {p.shopName}
                {p.bankAccount && ` · ${p.bankAccount}`}
              </Typography.Text>
            </Space>
          ),
        },
        { title: t("bill.amount"), dataIndex: "amount", width: 150, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
        { title: t("common.status"), dataIndex: "status", width: 120, render: (v: string) => <Tag>{v}</Tag> },
        {
          title: t("ar.posted"),
          width: 170,
          render: (_, p) => (p.posted ? <Tag color="success">{t("ar.postedYes")}</Tag> : <Tag color="warning">{t("ar.postedWaiting")}</Tag>),
        },
      ]}
    />
  );
}
